import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { useState } from "react";
import { DynamoMark } from "@/components/brand/DynamoMark";
import { authClient } from "@/lib/auth-client";
import { sessionQueryOptions } from "@/lib/session";

const AUTH_BASE: string =
  (import.meta.env.VITE_AUTH_BASE_URL as string | undefined) ??
  "https://dev-auth.propertyllama.com";

// Dev-only: "Continue with Microsoft" (Entra) sign-in. Gated by an explicit
// build flag set only in deploy-dev.yml — the var is absent on the prod build
// (and local `vite dev`), so the button never renders on prod.
const MS_LOGIN_ENABLED: boolean =
  (import.meta.env.VITE_ENABLE_MS_LOGIN as string | undefined) === "true";

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: loginSearchSchema,
  beforeLoad: async ({ context }) => {
    // Force a fresh /api/me instead of trusting a cached `null`. The _app guard
    // can bounce here on a transient 401 right after OAuth return (before the
    // session cookie is live); re-checking fresh lets a now-valid session
    // redirect straight into the app rather than stranding the user on /login.
    const session = await context.queryClient.fetchQuery({
      ...sessionQueryOptions,
      staleTime: 0,
    });
    if (session) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: "/opportunities",
        search: { q: "", sort: "delta", order: "desc", page: 1, filter: "all" },
        replace: true,
      });
    }
  },
  component: LoginPage,
});

function safeRedirect(raw: string | undefined, fallback: string): string {
  if (!raw) return fallback;
  try {
    const u = new URL(raw, window.location.origin);
    if (u.origin !== window.location.origin) return fallback;
    return u.toString();
  } catch {
    return fallback;
  }
}

// Basic email-shape check only. Server-side authorizeAppAccess decides who is
// actually invited and silently suppresses links to non-invited addresses, so
// the form no longer restricts by domain — this lets invited off-domain users
// (e.g. @gmail.com) request a sign-in link.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type DynamoState =
  | { kind: "collapsed" }
  | { kind: "form"; email: string; error: string | null; pending: boolean }
  | { kind: "sent"; email: string };

function LoginPage() {
  const { redirect } = Route.useSearch();
  const here = typeof window !== "undefined" ? window.location.origin : "";
  const target = safeRedirect(redirect, `${here}/opportunities`);
  const authHost = new URL(AUTH_BASE).host;

  const [googleError, setGoogleError] = useState<string | null>(null);
  const [googlePending, setGooglePending] = useState(false);
  const [microsoftError, setMicrosoftError] = useState<string | null>(null);
  const [microsoftPending, setMicrosoftPending] = useState(false);
  const [dynamo, setDynamo] = useState<DynamoState>({ kind: "collapsed" });

  const handleGoogle = async () => {
    setGoogleError(null);
    setGooglePending(true);
    try {
      await authClient.signIn.social({
        provider: "google",
        callbackURL: target,
      });
    } catch (err) {
      setGoogleError(err instanceof Error ? err.message : "Sign-in failed");
      setGooglePending(false);
    }
  };

  const handleMicrosoft = async () => {
    setMicrosoftError(null);
    setMicrosoftPending(true);
    try {
      await authClient.signIn.social({
        provider: "microsoft",
        callbackURL: target,
        errorCallbackURL: `${here}/login`,
      });
    } catch (err) {
      setMicrosoftError(err instanceof Error ? err.message : "Sign-in failed");
      setMicrosoftPending(false);
    }
  };

  const handleDynamoStart = () => {
    setDynamo({ kind: "form", email: "", error: null, pending: false });
  };

  const handleDynamoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (dynamo.kind !== "form") return;
    const email = dynamo.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      setDynamo({
        ...dynamo,
        error: "Enter a valid email address.",
      });
      return;
    }
    setDynamo({ ...dynamo, error: null, pending: true });
    try {
      await authClient.signIn.magicLink({ email, callbackURL: target });
      setDynamo({ kind: "sent", email });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Couldn't send sign-in link";
      setDynamo({ ...dynamo, error: msg, pending: false });
    }
  };

  const handleDynamoBack = () => setDynamo({ kind: "collapsed" });
  const handleDynamoResend = () => {
    if (dynamo.kind !== "sent") return;
    setDynamo({ kind: "form", email: dynamo.email, error: null, pending: false });
  };

  return (
    <div className="grid min-h-dvh grid-cols-1 md:grid-cols-2">
      <section className="flex flex-col justify-between bg-ink p-12 text-white">
        <DynamoMark className="h-12 w-auto text-gold" />
        <div>
          <h1 className="text-3xl font-semibold leading-tight">
            Re-underwrite competitor loans at 75% LTV against ARV.
          </h1>
          <p className="mt-4 max-w-md text-sm text-white/70">
            Switchback surfaces fresh CO bridge loans where Dynamo&apos;s terms beat the incumbent —
            daily, with a one-click mailer.
          </p>
        </div>
        <div className="text-xs text-white/40">
          Internal preview · Property Llama × Dynamo Capital
        </div>
      </section>
      <section className="flex items-center justify-center bg-white p-12">
        <div className="w-full max-w-sm">
          <h2 className="mb-2 text-xl font-semibold text-ink">Sign in</h2>
          <p className="mb-8 text-sm text-ink/60">
            Use your Property Llama or Dynamo Capital email. You&apos;ll be redirected to{" "}
            <code>{authHost}</code>.
          </p>

          <button
            type="button"
            onClick={() => void handleGoogle()}
            disabled={googlePending}
            className="block w-full rounded bg-ink px-4 py-3 text-center text-sm font-semibold text-gold hover:bg-ink/90 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            {googlePending ? "Redirecting…" : "Continue with Property Llama"}
          </button>
          {googleError ? <p className="mt-2 text-sm text-red-600">{googleError}</p> : null}

          {dynamo.kind === "collapsed" ? (
            <button
              type="button"
              onClick={handleDynamoStart}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded bg-ink px-4 py-3 text-center text-sm font-semibold text-gold hover:bg-ink/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
            >
              <DynamoMark className="h-4 w-auto text-gold" />
              <span>Continue with Dynamo</span>
            </button>
          ) : null}

          {dynamo.kind === "form" ? (
            <form onSubmit={(e) => void handleDynamoSubmit(e)} className="mt-3">
              <label className="block">
                <span className="block text-xs font-medium text-ink/60">Dynamo Capital email</span>
                <input
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={dynamo.email}
                  onChange={(e) => setDynamo({ ...dynamo, email: e.target.value, error: null })}
                  disabled={dynamo.pending}
                  placeholder="you@dynamocapital.com"
                  className="mt-1 block w-full rounded border border-ink/20 bg-white px-3 py-2 text-sm text-ink placeholder:text-ink/30 focus:border-ink/60 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-gold disabled:opacity-60"
                />
              </label>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="submit"
                  disabled={dynamo.pending}
                  className="flex-1 rounded bg-ink px-4 py-2 text-center text-sm font-semibold text-gold hover:bg-ink/90 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
                >
                  {dynamo.pending ? "Sending…" : "Email me a sign-in link"}
                </button>
                <button
                  type="button"
                  onClick={handleDynamoBack}
                  disabled={dynamo.pending}
                  className="rounded px-3 py-2 text-sm text-ink/60 hover:text-ink disabled:opacity-60"
                >
                  Back
                </button>
              </div>
              {dynamo.error ? <p className="mt-2 text-sm text-red-600">{dynamo.error}</p> : null}
            </form>
          ) : null}

          {dynamo.kind === "sent" ? (
            <div className="mt-3 rounded border border-ink/10 bg-ink/[0.02] p-4">
              <p className="text-sm font-medium text-ink">Check your email</p>
              <p className="mt-1 text-sm text-ink/70">
                We sent a sign-in link to <code className="text-ink">{dynamo.email}</code>. It
                expires in 5 minutes.
              </p>
              <button
                type="button"
                onClick={handleDynamoResend}
                className="mt-3 text-sm font-medium text-ink underline hover:text-ink/80"
              >
                Send another link
              </button>
            </div>
          ) : null}

          {MS_LOGIN_ENABLED ? (
            <>
              <button
                type="button"
                onClick={() => void handleMicrosoft()}
                disabled={microsoftPending}
                className="mt-3 block w-full rounded border border-ink/20 bg-white px-4 py-3 text-center text-sm font-semibold text-ink hover:bg-ink/[0.03] disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
              >
                {microsoftPending ? "Redirecting…" : "Continue with Microsoft"}
              </button>
              {microsoftError ? (
                <p className="mt-2 text-sm text-red-600">{microsoftError}</p>
              ) : null}
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
}
